import { FLOWS_ENUM, PAYMENT_METHODS } from 'src/common/enums';
import { getPaymentCategory } from 'src/common/paymentUtils';
import {
  CertifiedPaymentsPayload,
  FullPayPayload,
  GenericProviderPayload,
  HeartlandPayload,
  KalaPayload,
  PAYMENT_PROVIDER,
  PaymentMethod,
  RegistrantPayload,
  SnapPayPayload,
} from 'src/types/payment';
import { Person } from 'src/types/person';
import {
  CalculatedTot,
  CalculatedTotDetail,
  PaymentPeriod,
} from 'src/types/tax';

const buildRegistrantPayload = (
  person: Person | undefined
): RegistrantPayload | undefined => {
  if (!person) return undefined;
  return {
    first_name: person.firstName,
    last_name: person.lastName,
    email: person.email,
    address: person.mailingAddress,
    phone: person.phone,
  };
};

// Find the CalculatedTotDetail for a given period and license identifier. Requires a
// non-empty identifier on both sides before matching, so two licenses that both lack a
// license/application number don't spuriously match each other via "" === "".
const findTotDetail = (
  calculatedTaxData: CalculatedTot,
  periodName: string,
  licenseNumber: string | null | undefined,
  license: string | undefined,
  applicationNumber: string | undefined
): CalculatedTotDetail | undefined => {
  const licenseIdentifier = licenseNumber ?? license;
  return calculatedTaxData.details.find(
    (d) =>
      d.period === periodName &&
      ((!!licenseIdentifier && d.licence_no === licenseIdentifier) ||
        (!!applicationNumber && d.application_no === applicationNumber))
  );
};

const buildCertifiedPaymentsPayload = (
  person: Person | undefined,
  selectedLicenses: PaymentPeriod[],
  flow: FLOWS_ENUM,
  permitIdentifier: string,
  totalDue: number | string,
  calculatedTaxData: CalculatedTot | undefined
): CertifiedPaymentsPayload => {
  const registrant = buildRegistrantPayload(person);
  const fee_type = getPaymentCategory(flow)!;

  if (selectedLicenses.length > 0) {
    const isTot = flow === FLOWS_ENUM.PAY_TOT;
    const payment_periods = selectedLicenses.flatMap((period) =>
      period.licenses.map((license) => {
        const licenseNum =
          license.licenseNumber ?? license.license ?? permitIdentifier;
        const base = {
          license: licenseNum,
          formatted_period: period.name,
          amount: period.totalDueForPeriod ?? Number(totalDue),
          fee_type,
          address: license.address || undefined,
        };

        if (!isTot || !calculatedTaxData) {
          return base;
        }

        const detail = findTotDetail(
          calculatedTaxData,
          period.name,
          license.licenseNumber,
          license.license,
          license.applicationNumber
        );
        if (!detail) return base;

        return {
          ...base,
          amount: detail.total,
          tax_due: detail.totDue,
          late_penalties: detail.latePenalties,
          accrued_interest: detail.accruedInterest,
        };
      })
    );
    return { registrant, payment_periods };
  }

  return {
    registrant,
    payment_periods: [
      {
        license: permitIdentifier,
        formatted_period: '',
        amount: Number(totalDue),
        fee_type,
      },
    ],
  };
};

const buildHeartlandPayload = (
  person: Person | undefined
): HeartlandPayload => ({
  registrant: buildRegistrantPayload(person),
});

const buildFullPayPayload = (
  selectedLicenses: PaymentPeriod[],
  flow: FLOWS_ENUM,
  permitIdentifier: string
): FullPayPayload => {
  if (flow === FLOWS_ENUM.PAY_TOT && selectedLicenses.length > 0) {
    const payment_periods = selectedLicenses.flatMap((period) =>
      period.licenses.map((license) => ({
        license_id: license.license ?? license.applicationNumber,
        license_number:
          license.licenseNumber ??
          license.license ??
          license.applicationNumber ??
          permitIdentifier,
        formatted_period: period.name,
      }))
    );
    return { payment_periods };
  }

  return {
    payment_periods: permitIdentifier
      ? [
          {
            license_id: permitIdentifier,
            license_number: permitIdentifier,
            formatted_period: '',
          },
        ]
      : [],
  };
};

const buildSnapPayPayload = (paymentMethod: PaymentMethod): SnapPayPayload => ({
  paymentMode:
    paymentMethod === PAYMENT_METHODS.ACH_DIRECT_DEBIT ? 'ACH' : 'CC',
});

const buildKalaPayload = (
  paymentMethod: PaymentMethod,
  selectedLicenses: PaymentPeriod[],
  flow: FLOWS_ENUM,
  permitIdentifier: string,
  totalDue: number | string
): KalaPayload => {
  const payment_method =
    paymentMethod === PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD
      ? 'credit_card'
      : 'ach';
  const fee_type = getPaymentCategory(flow)!;

  if (selectedLicenses.length > 0) {
    const payment_periods = selectedLicenses.flatMap((period) =>
      period.licenses.map((license) => ({
        license: license.licenseNumber ?? license.license ?? permitIdentifier,
        amount: period.totalDueForPeriod ?? Number(totalDue),
        fee_type,
      }))
    );
    return { payment_method, payment_periods };
  }

  return {
    payment_method,
    payment_periods: [
      { license: permitIdentifier, amount: Number(totalDue), fee_type },
    ],
  };
};

export const buildGenericProviderPayload = (
  provider: PAYMENT_PROVIDER | undefined,
  person: Person | undefined,
  selectedLicenses: PaymentPeriod[],
  flow: FLOWS_ENUM,
  permitIdentifier: string,
  totalDue: number | string,
  paymentMethod: PaymentMethod,
  calculatedTaxData: CalculatedTot | undefined
): GenericProviderPayload => {
  switch (provider) {
    case PAYMENT_PROVIDER.CERTIFIED_PAYMENTS:
      return buildCertifiedPaymentsPayload(
        person,
        selectedLicenses,
        flow,
        permitIdentifier,
        totalDue,
        calculatedTaxData
      );
    case PAYMENT_PROVIDER.HEARTLAND:
      return buildHeartlandPayload(person);
    case PAYMENT_PROVIDER.FULLPAY:
      return buildFullPayPayload(selectedLicenses, flow, permitIdentifier);
    case PAYMENT_PROVIDER.KALA:
      return buildKalaPayload(
        paymentMethod,
        selectedLicenses,
        flow,
        permitIdentifier,
        totalDue
      );
    case PAYMENT_PROVIDER.SNAPPAY:
      return buildSnapPayPayload(paymentMethod);
    default:
      return null;
  }
};
